import React, { useState, useEffect } from 'react';
import LoadingScreen from './components/LoadingScreen';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import AnatomySection from './components/AnatomySection';
import ModelsSeparator from './components/ModelsSeparator';
import ManifestoSection from './components/ManifestoSection';
import ThreeModelsSection from './components/ThreeModelsSection';
import LifestyleSection from './components/LifestyleSection';
import SpecsSection from './components/SpecsSection';
import WaitlistSection from './components/WaitlistSection';
import OperatorPanel from './components/OperatorPanel';
import Footer from './components/Footer';
import './App.css';

function App() {
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [operatorOpen, setOperatorOpen] = useState(false);

  useEffect(() => {
    if (loadingComplete) {
      initParticleSystem();
      initCustomCursor();
    }
  }, [loadingComplete]);

  return (
    <>
      <LoadingScreen onComplete={() => setLoadingComplete(true)} />
      <div id="eterna-main">
        <Navbar
          onOperatorOpen={() => setOperatorOpen(true)}
        />
        <HeroSection loadingComplete={loadingComplete} />
        <AnatomySection />
        <ModelsSeparator />
        <ManifestoSection />
        <ThreeModelsSection />
        <LifestyleSection />
        <SpecsSection />
        <WaitlistSection />
        <Footer />
      </div>
      <OperatorPanel
        isOpen={operatorOpen}
        onClose={() => setOperatorOpen(false)}
      />
    </>
  );
}

function initParticleSystem() {
  if (window.innerWidth < 768) return;
  if (document.getElementById('eterna-particles')) return;

  const canvas = document.createElement('canvas');
  canvas.id = 'eterna-particles';
  canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;z-index:9999;pointer-events:none;';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  const SPACING = 28;
  const RADIUS = 120;
  let mouse = { x: -999, y: -999 };
  document.addEventListener('mousemove', e => { mouse.x = e.clientX; mouse.y = e.clientY; });

  function drawDots() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let x = 0; x < canvas.width; x += SPACING) {
      for (let y = 0; y < canvas.height; y += SPACING) {
        const dist = Math.hypot(x - mouse.x, y - mouse.y);
        if (dist < RADIUS) {
          const intensity = 1 - dist / RADIUS;
          const size = 1 + intensity * 2;
          const opacity = 0.08 + intensity * 0.75;
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(10,10,10,${opacity})`;
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(x, y, 1, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(10,10,10,0.08)';
          ctx.fill();
        }
      }
    }
    requestAnimationFrame(drawDots);
  }
  drawDots();
  window.addEventListener('resize', () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  });
}

function initCustomCursor() {
  if (document.getElementById('custom-cursor')) return;

  const cursor = document.createElement('div');
  cursor.id = 'custom-cursor';
  cursor.style.cssText = `
    position: fixed;
    width: 10px;
    height: 10px;
    background: #0EA5E9;
    border-radius: 50%;
    pointer-events: none;
    z-index: 999999;
    transform: translate(-50%, -50%);
    transition: width 250ms ease, height 250ms ease, background 250ms ease, border 250ms ease;
    will-change: left, top;
    left: -100px;
    top: -100px;
  `;
  document.body.appendChild(cursor);
  document.body.style.cursor = 'none';

  const style = document.createElement('style');
  style.textContent = `
    *, *::before, *::after { cursor: none !important; }
    #custom-cursor { cursor: none !important; }
  `;
  document.head.appendChild(style);

  let cursorX = -100, cursorY = -100;
  let targetX = -100, targetY = -100;

  document.addEventListener('mousemove', (e) => {
    targetX = e.clientX;
    targetY = e.clientY;
  });

  document.addEventListener('touchmove', (e) => {
    const touch = e.touches[0];
    targetX = touch.clientX;
    targetY = touch.clientY;
  }, { passive: true });

  document.addEventListener('touchstart', (e) => {
    const touch = e.touches[0];
    targetX = touch.clientX;
    targetY = touch.clientY;
    cursor.style.width = '44px';
    cursor.style.height = '44px';
    cursor.style.background = 'transparent';
    cursor.style.border = '1.5px solid #0EA5E9';
  }, { passive: true });

  document.addEventListener('touchend', () => {
    cursor.style.width = '10px';
    cursor.style.height = '10px';
    cursor.style.background = '#0EA5E9';
    cursor.style.border = 'none';
  }, { passive: true });

  function animateCursor() {
    cursorX += (targetX - cursorX) * 0.15;
    cursorY += (targetY - cursorY) * 0.15;
    cursor.style.left = cursorX + 'px';
    cursor.style.top = cursorY + 'px';
    requestAnimationFrame(animateCursor);
  }
  animateCursor();

  function attachHover(el) {
    el.addEventListener('mouseenter', () => {
      cursor.style.width = '44px';
      cursor.style.height = '44px';
      cursor.style.background = 'transparent';
      cursor.style.border = '1.5px solid #0EA5E9';
      cursor.style.opacity = '0.6';
    });
    el.addEventListener('mouseleave', () => {
      cursor.style.width = '10px';
      cursor.style.height = '10px';
      cursor.style.background = '#0EA5E9';
      cursor.style.border = 'none';
      cursor.style.opacity = '1';
    });
  }

  document.querySelectorAll('a, button, [role="button"], input, select, textarea, label').forEach(attachHover);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL'].includes(node.tagName)) {
            attachHover(node);
          }
          node.querySelectorAll && node.querySelectorAll('a, button, [role="button"], input, select, textarea, label').forEach(attachHover);
        }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export default App;
